import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TooltipModule } from 'primeng/tooltip';
import { AppConfigurator } from './app.configurator';
import { LayoutService } from '../service/layout.service';
import { ApiService } from '@/pages/service/core/api.service';
import { VerticalConfigService } from '@/pages/service/core/vertical-config.service';
import { UserMenu } from './app.user-menu';
import { hasAnyRole } from './role.util';
import { RoutineTimerService } from '@/pages/routines/routine-timer.service';
import { SessionKeepAliveService } from '@/pages/service/core/session-keepalive.service';
import { PermissionStore } from '@/pages/service/core/permission-store';
import { firstAllowedRoute } from './submenu-panel/menu-nav.util';

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, StyleClassModule, AutoCompleteModule, TooltipModule, AppConfigurator, UserMenu],
    template: ` <div class="layout-topbar">
        <div class="layout-topbar-logo-container">
            <button class="layout-menu-button layout-topbar-action" (click)="layoutService.onMenuToggle()">
                <i class="pi pi-bars"></i>
            </button>
            <a class="layout-topbar-logo" (click)="goHome()">
                <img [src]="brand.logoObjectUrl() || 'assets/logo-grow.png'" alt="Logo">
            </a>
        </div>

        <div class="layout-topbar-right">
            <!-- 🔎 Search my cases (type-ahead → backend, scoped to the logged-in user) -->
            <p-autoComplete appendTo="body" *ngIf="showSearch" class="topbar-search" [(ngModel)]="searchModel"
                            [suggestions]="suggestions" (completeMethod)="searchCases($event)"
                            (onSelect)="onSelectCase($event)" optionLabel="label" [delay]="300" [minLength]="2"
                            placeholder="Search my cases…" [showClear]="true">
                <ng-template let-c pTemplate="item">
                    <div class="topbar-search__opt">
                        <span class="topbar-search__name">{{ c.label }}</span>
                        <small class="topbar-search__sub" *ngIf="c.sub">{{ c.sub }}</small>
                    </div>
                </ng-template>
            </p-autoComplete>

            <!-- Live routine timer — on every page while a routine is in progress; sits before the bell. -->
            <a *ngIf="routineTimer.activeRun() as run" class="topbar-routine"
               (click)="goToRoutine()" [pTooltip]="run.routineName + ' — in progress'">
                <i class="pi pi-stopwatch"></i>
                <span class="topbar-routine__time">{{ routineTimer.elapsed() }}</span>
            </a>

            <!-- Account menu (notification bell + profile avatar → My Profile / Settings / Logout). -->
            <app-user-menu></app-user-menu>
        </div>

    </div>`,
    styles: [`
        .layout-topbar-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
        .topbar-search { width: 280px; max-width: 36vw; }
        .topbar-search__opt { display: flex; flex-direction: column; }
        .topbar-search__name { font-size: var(--gh-fs-14); }
        .topbar-search__sub { color: var(--text-color-secondary, var(--gh-text-3)); font-size: var(--gh-fs-12); }
        .topbar-routine { display: inline-flex; align-items: center; gap: 6px; padding: 5px 10px; border-radius: var(--gh-r-pill); background: var(--gh-primary-wash); color: var(--gh-primary); font-weight: 700; font-size: var(--gh-fs-13); cursor: pointer; }
        .topbar-routine:hover { background: var(--gh-primary-100); }
        .topbar-routine__time { font-variant-numeric: tabular-nums; min-width: 34px; }
    `]
})
export class AppTopbar implements OnInit {
    // "Search my cases" type-ahead — agents/managers/admins (not super admins).
    showSearch = !hasAnyRole('ROLE_SUPER_ADMIN');
    searchModel: any = null;
    suggestions: any[] = [];

    constructor(public layoutService: LayoutService, private router: Router, private api: ApiService,
                public brand: VerticalConfigService, public routineTimer: RoutineTimerService,
                private perms: PermissionStore,
                private sessionKeepAlive: SessionKeepAliveService) {}

    ngOnInit(): void {
        // Routines are a company/agent feature — a super admin has no company, so polling
        // /api/routines/v1/today just 401s. Skip the global timer for super admins.
        if (!hasAnyRole('ROLE_SUPER_ADMIN')) this.routineTimer.startTracking();
        // Keep the session alive during idle stretches (long admin screens like migration setup) for
        // every role, so the token doesn't lapse to a 401.
        this.sessionKeepAlive.start();
    }

    /** Open the routine planner (calendar → Routine tab). */
    goToRoutine(): void { this.router.navigate(['/calendar'], { queryParams: { tab: 'routine' } }); }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }
    /** Logo → the user's landing page ("the first page of first load"), respecting their permission
     *  template. Mirrors the post-login rule (Company Admin → Lead Dashboard, everyone else →
     *  My Work); if the template denies that page, fall back to the first menu page the user IS
     *  granted — so a restricted user lands there instead of being bounced to /notfound. */
    goHome(){
        const preferred = hasAnyRole('ROLE_COMPANY_ADMIN') ? '/lead-dashboard' : '/my-work';
        if (this.perms.canVisit(preferred)) { this.router.navigateByUrl(preferred); return; }
        const dest = firstAllowedRoute(this.perms, { proMode: this.brand.proMode() });
        if (dest) {
            this.router.navigate(dest.routerLink, dest.queryParams ? { queryParams: dest.queryParams } : {});
        } else {
            this.router.navigate(['/my-work']);
        }
    }

    /** Type-ahead over the user's own cases (assigned to them) — calls the backend. */
    searchCases(event: any): void {
        const q = (event?.query ?? '').trim();
        if (!q) { this.suggestions = []; return; }
        this.api.get<any>(`/api/agent/my-work/search?q=${encodeURIComponent(q)}`).subscribe({
            next: (res: any) => {
                const list = res?.data ?? res ?? [];
                this.suggestions = (Array.isArray(list) ? list : []).map((c: any) => ({
                    label: this.caseLabel(c),
                    sub: [c.currentPhaseName, c.currentPhaseStatusName].filter(Boolean).join(' · '),
                    waId: c.waId,
                    caseId: c.caseId,
                }));
            },
            error: () => { this.suggestions = []; }
        });
    }

    /** Open the picked case's customer flow. */
    onSelectCase(event: any): void {
        const v = event?.value;
        if (v?.waId) {
            this.router.navigate(['/customer-flows', v.waId], { queryParams: { caseId: v.caseId } });
        }
        this.searchModel = null;
        this.suggestions = [];
    }

    /** Display label: captured name (· phone), else the phone/number. */
    private caseLabel(c: any): string {
        const ed = (c?.extractedData && typeof c.extractedData === 'object') ? c.extractedData : {};
        const pick = (keys: string[]) => {
            for (const k of keys) { const v = ed[k]; if (v != null && String(v).trim()) return String(v).trim(); }
            return '';
        };
        const name = pick(['name', 'user_name', 'full_name', 'customer_name']);
        const phone = pick(['phone', 'user_phone', 'phone_number', 'mobile', 'contact']) || c?.waId || '';
        if (name) return (phone && phone !== name) ? `${name} · ${phone}` : name;
        return phone || 'Case';
    }
}
